/**
 * PERIOD SELECTOR COMPONENT
 * Composant sélecteur de période réutilisable pour les statistiques
 */

import { Select } from './select';
import { cn } from '@/lib/utils';

/**
 * Options de période disponibles
 */
export type PeriodValue = '7d' | '30d' | '90d' | '6m' | '12m' | 'all';

export interface PeriodOption {
  value: PeriodValue;
  label: string;
  days: number | null; // null = pas de limite
}

export const PERIOD_OPTIONS: PeriodOption[] = [
  { value: '7d', label: '7 derniers jours', days: 7 },
  { value: '30d', label: '30 derniers jours', days: 30 },
  { value: '90d', label: '90 derniers jours', days: 90 },
  { value: '6m', label: '6 derniers mois', days: 180 },
  { value: '12m', label: '12 derniers mois', days: 365 },
  { value: 'all', label: 'Tout', days: null },
];

/**
 * Calcule la date de début en fonction de la période sélectionnée
 * @param period - La période sélectionnée
 * @returns Date de début ou null si pas de limite
 */
export function getStartDateForPeriod(period: PeriodValue): Date | null {
  const option = PERIOD_OPTIONS.find((opt) => opt.value === period);
  if (!option || option.days === null) return null;

  const startDate = new Date();
  startDate.setDate(startDate.getDate() - option.days);
  startDate.setHours(0, 0, 0, 0);
  return startDate;
}

interface PeriodSelectorProps {
  /**
   * Valeur actuellement sélectionnée
   */
  value: PeriodValue;

  /**
   * Callback appelé lors du changement de période
   */
  onChange: (value: PeriodValue) => void;

  /**
   * Classes CSS additionnelles
   */
  className?: string;

  /**
   * Variante du select
   */
  variant?: 'default' | 'inline';
}

/**
 * Sélecteur de période pour les statistiques
 */
function PeriodSelector({ value, onChange, className, variant = 'inline' }: PeriodSelectorProps) {
  const options = PERIOD_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value as PeriodValue)}
      options={options}
      variant={variant}
      className={cn('w-auto min-w-40', className)}
    />
  );
}

export { PeriodSelector };
